import React, { useCallback, useRef, useState } from 'react';
import forgotPasswordSx from './ForgotPassword.styles';
import {
	Button,
	Card,
	FormControl,
	Grid,
	Input,
	Link,
	Typography,
	useMediaQuery,
	Box,
} from "@mui/material";
import { EnhancedEncryptionOutlined } from "@mui/icons-material";
import { Link as RouterLink } from '@tanstack/react-router';
import api from 'api';
import useRequest from 'hooks/useRequest';
import { useTheme } from '@mui/material/styles';



const ForgotPassword = () => {
	const formRef             = useRef(null),
		  [ email, setEmail ] = useState('');
	const theme = useTheme();
	const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
	const sx = forgotPasswordSx(theme);

	const sendEmailResetRequest = useCallback(() => {
		return api.post('/auth/reset-password', { email });
	}, [ email ]);

	const {
			  loading,
			  hasLoaded,
			  error,
			  refetch: requestPasswordReset,
		  } = useRequest({ requestFunction: sendEmailResetRequest, lazy: true });


	const handleChangeEmail = e => {
		setEmail(e.target.value);
	};


	const handleSubmit = async (e) => {
		e.preventDefault();
		await requestPasswordReset();
	};

	return (
		<Box sx={sx.root}>
			<Box sx={sx.child}>
				<Grid container spacing={0} sx={sx.fullHeightWidth}>
					{!isMdDown && (
						<Grid size={{ md: 5 }} sx={sx.leftCardWrapper}>
							<Card sx={sx.leftCard} raised>
								<Box sx={sx.leftCardInner}>
									<Box>
										<EnhancedEncryptionOutlined sx={sx.leftCardIcon} />
									</Box>
									<Box sx={sx.marginBottom}>
										<Typography variant="h4">Forgot Password</Typography>
									</Box>
									<Box>
										<Typography>
											Hey, it happens to the best of us. Enter your email address in the form
											to the right and we'll send you an email to reset your password so you can
											get back up and running. If you want to get back to the homepage, click{' '}
											<Link component={RouterLink} to="/" color="inherit">here</Link>.
										</Typography>
									</Box>
								</Box>
							</Card>
						</Grid>
					)}

					<Grid size={{ xs: 12, md: 7 }} sx={sx.rightCardWrapper}>
						<Card sx={sx.rightCard} raised>
							{!hasLoaded && (
								<>
									{isMdDown && (
										<Box>
											<Typography>
												Hey, it happens to the best of us. Enter your email address in the form
												and
												we'll send you a link to reset your password so you can get back up and
												running. If you want to get back to the homepage, click{' '}
												<Link component={RouterLink} to="/" color="inherit">here</Link>.
											</Typography>
										</Box>
									)}

									<form onSubmit={handleSubmit} ref={formRef}>
										{Boolean(error) && (
											<Typography>
												Oops, it looks like there was an error sending your request. Please make
												sure you've entered a valid email address and try again. If you've
												recently
												submitted a password reset request, please wait a few minutes before
												submitting another.
											</Typography>
										)}

										<FormControl fullWidth margin="normal">
											<Input
												type="text"
												placeholder="Email Address"
												onChange={handleChangeEmail}
												value={email}
												sx={sx.inputs}
												disabled={loading}
											/>
										</FormControl>

										<FormControl fullWidth margin="normal" sx={sx.inputs}>
											<Button
												variant="contained"
												color="primary"
												sx={{ width: '100%' }}
												type="submit"
												disabled={loading}
											>
												Submit
											</Button>
										</FormControl>
									</form>
								</>
							)}

							{hasLoaded && (
								<>
									<Typography sx={sx.marginBottom}>
										We've received your request. If the email address you entered is tied to a
										valid account, you'll receive an email with instructions to reset your password
										shortly. The link contained in the email is only valid for 1 hour.
									</Typography>

									<Typography>
										Click{' '}
										<Link component={RouterLink} to="/" color="inherit">here</Link>
										{' '}to return to the homepage.
									</Typography>
								</>
							)}
						</Card>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
};


export default ForgotPassword;
